import {css, cva} from "@panda/css";

import {BuildBigIcon, ClockIcon} from "@pg-design/icons-module";
import {Range, RangeColorVariant} from "@pg-design/range-module";
import {Text} from "@pg-design/text-module";
import {useHorizontalDragScroll} from "@pg-mono/hooks";
import {compact, noop, uniq} from "@pg-mono/nodash";
import {formatDate} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useConstructionCompletionPercent} from "../../../property/hooks/useConstructionCompletionPercent";
import {IRangeQuery} from "../../../request/IRangeQuery";
import {getConstructionGalleryTagLabel, IConstructionGalleryResponse} from "../../types/ConstructionGallery";

interface IProps {
    constructionGallery: IConstructionGalleryResponse;
    constructionDateRange: IRangeQuery<string>;
    constructionEndDate?: string | null;
    policamLinksMonths?: string[];
    selectedDate: string | null;
    onDateSelect: (date: string | null) => void;
}

export const ConstructionTimelineSection = (props: IProps) => {
    const {constructionGallery, constructionDateRange, constructionEndDate, policamLinksMonths = [], selectedDate, onDateSelect} = props;

    const pillsContainerRef = useHorizontalDragScroll();
    const {isMobile} = useUserDevice();
    const {calculateConstructionCompletionPercent} = useConstructionCompletionPercent();

    const constructionGalleryMonths = compact(constructionGallery.map((item) => item?.tags?.tag_date?.slice(0, 7)));
    const uniqueDates = uniq([...constructionGalleryMonths, ...policamLinksMonths]).sort((prev, next) => next.localeCompare(prev));

    const latestEntry = [...constructionGallery]
        .filter((item) => item?.tags?.tag_date && item?.tags?.tag_status)
        .sort((prev, next) => (next?.tags?.tag_date ?? "").localeCompare(prev?.tags?.tag_date ?? ""))[0];

    const latestStatus = latestEntry?.tags?.tag_status;
    const completionPercent = calculateConstructionCompletionPercent(constructionEndDate, constructionDateRange);
    // Data-based % can hit 100 before the gallery's latest photo is tagged as finished — force the final stage so the two never contradict each other.
    const effectiveStatus = completionPercent === 100 ? 6 : latestStatus;
    const currentStageName = effectiveStatus ? getConstructionGalleryTagLabel(effectiveStatus) : null;

    return (
        <div className={sectionStyle}>
            <div className={infoStyle}>
                {completionPercent !== null && (
                    <Range
                        name="constructionProgress"
                        value={{upper: completionPercent}}
                        onChange={noop}
                        min={0}
                        max={100}
                        step={1}
                        disabled
                        hideLabels
                        colorVariant={RangeColorVariant.GREEN}
                        size={isMobile ? "sm" : "md"}
                        className={progressSliderStyle}
                    />
                )}

                <div className={infoTextsStyle}>
                    {currentStageName && (
                        <div className={infoRowStyle}>
                            <BuildBigIcon size="1.6" fill="white" className={stageIconStyle} />
                            <Text variant="body_copy_2" className={whiteTextStyle}>
                                Aktualny etap: <strong>{currentStageName}</strong>
                            </Text>
                        </div>
                    )}
                    {completionPercent !== null && (
                        <div className={infoRowStyle}>
                            <ClockIcon size="1.6" fill="white" />
                            <Text variant="body_copy_2" className={whiteTextStyle}>
                                <strong>Ukończono {completionPercent}%</strong>
                            </Text>
                        </div>
                    )}
                </div>
            </div>

            {uniqueDates.length > 0 && (
                <div ref={pillsContainerRef} className={pillsContainerStyle}>
                    {uniqueDates.map((date) => (
                        <button
                            key={date}
                            type="button"
                            aria-pressed={selectedDate === date}
                            className={pillStyle({active: selectedDate === date})}
                            onClick={() => onDateSelect(selectedDate === date ? null : date)}
                        >
                            <Text variant="body_copy_2" className={whiteTextStyle}>
                                {formatDate(date, "LLLL yyyy")}
                            </Text>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const sectionStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 3,
    px: 1.5,
    mb: 3,

    md: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between"
    }
});

const infoStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 3
});

const progressSliderStyle = css({
    flexShrink: 0,
    width: "64px",
    "--range-thumb-size": "16px",

    md: {
        width: "146px",
        "--range-thumb-size": "2.4rem"
    }
});

const infoTextsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5
});

const infoRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});

const stageIconStyle = css({
    flexShrink: 0
});

const whiteTextStyle = css({
    color: "var(--colors-white)"
});

const pillsContainerStyle = css({
    display: "flex",
    backgroundColor: "var(--colors-gray-800)",
    borderRadius: "360px",
    p: 0.5,
    overflowX: "auto",
    minWidth: 0,
    maxWidth: "100%",

    md: {
        maxWidth: "50%",
        cursor: "grab",
        "&::-webkit-scrollbar": {height: "4px"},
        "&::-webkit-scrollbar-thumb": {backgroundColor: "rgba(255,255,255,0.4)"},
        "&::-webkit-scrollbar-track": {backgroundColor: "transparent"}
    }
});

const pillStyle = cva({
    base: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "40px",
        width: "136px",
        px: 2,
        borderRadius: "180px",
        flexShrink: 0,
        cursor: "pointer",
        border: "1px solid transparent",
        backgroundColor: "transparent",
        transition: "background-color 0.15s ease, border-color 0.15s ease",

        md: {
            width: "160px"
        }
    },
    variants: {
        active: {
            true: {
                backgroundColor: "var(--colors-secondary)",
                borderColor: "var(--colors-white)",
                boxShadow: "0px 4px 2px rgba(0, 0, 0, 0.25)"
            }
        }
    }
});
import {css, cva} from "@panda/css";

import {BuildBigIcon, ClockIcon} from "@pg-design/icons-module";
import {Range, RangeColorVariant} from "@pg-design/range-module";
import {Text} from "@pg-design/text-module";
import {useHorizontalDragScroll} from "@pg-mono/hooks";
import {compact, noop, uniq} from "@pg-mono/nodash";
import {formatDate} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useConstructionCompletionPercent} from "../../../property/hooks/useConstructionCompletionPercent";
import {IRangeQuery} from "../../../request/IRangeQuery";
import {getConstructionGalleryTagLabel, IConstructionGalleryResponse} from "../../types/ConstructionGallery";

interface IProps {
    constructionGallery: IConstructionGalleryResponse;
    constructionDateRange: IRangeQuery<string>;
    constructionEndDate?: string | null;
    policamLinksMonths?: string[];
    selectedDate: string | null;
    onDateSelect: (date: string | null) => void;
}

export const ConstructionTimelineSection = (props: IProps) => {
    const {constructionGallery, constructionDateRange, constructionEndDate, policamLinksMonths = [], selectedDate, onDateSelect} = props;

    const pillsContainerRef = useHorizontalDragScroll();
    const {isMobile} = useUserDevice();
    const {calculateConstructionCompletionPercent} = useConstructionCompletionPercent();

    const constructionGalleryMonths = compact(constructionGallery.map((item) => item?.tags?.tag_date?.slice(0, 7)));
    const uniqueDates = uniq([...constructionGalleryMonths, ...policamLinksMonths]).sort((prev, next) => next.localeCompare(prev));

    const latestEntry = [...constructionGallery]
        .filter((item) => item?.tags?.tag_date && item?.tags?.tag_status)
        .sort((prev, next) => (next?.tags?.tag_date ?? "").localeCompare(prev?.tags?.tag_date ?? ""))[0];

    const latestStatus = latestEntry?.tags?.tag_status;
    const completionPercent = calculateConstructionCompletionPercent(constructionEndDate, constructionDateRange);
    // Data-based % can hit 100 before the gallery's latest photo is tagged as finished — force the final stage so the two never contradict each other.
    const effectiveStatus = completionPercent === 100 ? 6 : latestStatus;
    const currentStageName = effectiveStatus ? getConstructionGalleryTagLabel(effectiveStatus) : null;

    return (
        <div className={sectionStyle}>
            <div className={infoStyle}>
                {completionPercent !== null && (
                    <Range
                        name="constructionProgress"
                        value={{upper: completionPercent}}
                        onChange={noop}
                        min={0}
                        max={100}
                        step={1}
                        disabled
                        hideLabels
                        colorVariant={RangeColorVariant.GREEN}
                        size={isMobile ? "sm" : "md"}
                        className={progressSliderStyle}
                    />
                )}

                <div className={infoTextsStyle}>
                    {currentStageName && (
                        <div className={infoRowStyle}>
                            <BuildBigIcon size="1.6" fill="white" className={stageIconStyle} />
                            <Text variant="body_copy_2" className={whiteTextStyle}>
                                Aktualny etap: <strong>{currentStageName}</strong>
                            </Text>
                        </div>
                    )}
                    {completionPercent !== null && (
                        <div className={infoRowStyle}>
                            <ClockIcon size="1.6" fill="white" />
                            <Text variant="body_copy_2" className={whiteTextStyle}>
                                <strong>Ukończono {completionPercent}%</strong>
                            </Text>
                        </div>
                    )}
                </div>
            </div>

            {uniqueDates.length > 0 && (
                <div ref={pillsContainerRef} className={pillsContainerStyle}>
                    {uniqueDates.map((date) => (
                        <button
                            key={date}
                            type="button"
                            aria-pressed={selectedDate === date}
                            className={pillStyle({active: selectedDate === date})}
                            onClick={() => onDateSelect(selectedDate === date ? null : date)}
                        >
                            <Text variant="body_copy_2" className={whiteTextStyle}>
                                {formatDate(date, "LLLL yyyy")}
                            </Text>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const sectionStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 3,
    px: 1.5,
    mb: 3,

    md: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between"
    }
});

const infoStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 3
});

const progressSliderStyle = css({
    flexShrink: 0,
    width: "64px",
    "--range-thumb-size": "16px",

    md: {
        width: "146px",
        "--range-thumb-size": "2.4rem"
    }
});

const infoTextsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5
});

const infoRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});

const stageIconStyle = css({
    flexShrink: 0
});

const whiteTextStyle = css({
    color: "var(--colors-white)"
});

const pillsContainerStyle = css({
    display: "flex",
    backgroundColor: "var(--colors-gray-800)",
    borderRadius: "360px",
    p: 0.5,
    overflowX: "auto",
    minWidth: 0,
    maxWidth: "100%",

    md: {
        maxWidth: "50%",
        cursor: "grab",
        "&::-webkit-scrollbar": {height: "4px"},
        "&::-webkit-scrollbar-thumb": {backgroundColor: "rgba(255,255,255,0.4)"},
        "&::-webkit-scrollbar-track": {backgroundColor: "transparent"}
    }
});

const pillStyle = cva({
    base: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "40px",
        width: "136px",
        px: 2,
        borderRadius: "180px",
        flexShrink: 0,
        cursor: "pointer",
        border: "1px solid transparent",
        backgroundColor: "transparent",
        transition: "background-color 0.15s ease, border-color 0.15s ease",

        md: {
            width: "160px"
        }
    },
    variants: {
        active: {
            true: {
                backgroundColor: "var(--colors-secondary)",
                borderColor: "var(--colors-white)",
                boxShadow: "0px 4px 2px rgba(0, 0, 0, 0.25)"
            }
        }
    }
});
